import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen, Users, Calendar, Plus, ArrowRight } from "lucide-react";
import { useSubjectClasses } from "@/hooks/useSubjectClasses";
import { format, differenceInDays } from "date-fns";

export const MyClassesWidget = () => {
  const navigate = useNavigate();
  const { data: classes, isLoading } = useSubjectClasses(true);

  const activeClasses = classes?.filter(c => c.status === "Active" || c.status === "Teaching Complete") || [];

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            My Active Classes
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => navigate("/subject-classes")}>
            <Plus className="h-4 w-4 mr-1" />
            New
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {activeClasses.length === 0 ? (
          <div className="text-center py-6">
            <BookOpen className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground text-sm">No active classes</p>
            <Button
              size="sm"
              variant="link"
              onClick={() => navigate("/subject-classes")}
              className="mt-2"
            >
              Create your first class
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {activeClasses.slice(0, 3).map(classItem => {
              const daysElapsed = differenceInDays(new Date(), new Date(classItem.start_date));
              
              return (
                <div
                  key={classItem.id}
                  className="p-3 rounded-lg border hover:border-primary/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/subject-classes/${classItem.id}`)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium">{classItem.class_name}</p>
                      <p className="text-sm text-muted-foreground">{classItem.subject?.name}</p>
                    </div>
                    <Badge
                      variant="outline"
                      className={
                        classItem.status === "Active"
                          ? "bg-green-500/10 text-green-500 border-green-500/20"
                          : "bg-blue-500/10 text-blue-500 border-blue-500/20"
                      }
                    >
                      {classItem.status === "Teaching Complete" ? "Exams" : "Active"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {classItem.students_count || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Day {classItem.total_class_days}
                    </span>
                    <span className="text-xs">
                      Started {daysElapsed}d ago
                    </span>
                  </div>
                  <Button
                    size="sm"
                    className="w-full mt-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/subject-classes/${classItem.id}`);
                    }}
                  >
                    Mark Today's Attendance
                  </Button>
                </div>
              );
            })}

            {activeClasses.length > 3 && (
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => navigate("/subject-classes")}
              >
                View all {activeClasses.length} classes
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
